import { computed, ref } from '@nuxtjs/composition-api';
import { useDiscountStore } from '~/stores/useDiscountStore';
import useTracking from '~/composable/tracking/useTracking';

export default function useDiscounts (appContext) {
  const { $hubble: hubbleClient, $pinia: piniaStore } = appContext;
  if (!appContext || !hubbleClient) {
    throw new Error('You must provide the nuxt app context to use this composable');
  }
  const discountStore = useDiscountStore(piniaStore)
  // Reactive state
  const userName = ref('');
  const password = ref('');
  const loading = ref(false);
  const cartID = ref(null);
  const productKey = ref(null);
  const sku = ref(null);
  const inputError = ref(null);
  const notEligibleError = ref(null);
  const errorState = ref(false);
  const noDiscountError = ref(null);
  const mbbCustomerError = ref(null)
  const mcSubUserError = ref(null)
  const discountCalculationCompleted = ref(false);
  const discountResponse = ref({});

  // Error messages
  const ERROR_INVALID_INPUT = 'Refs productKey ref, sku, userName, password, cartID of this composable must have a value to use this function';
  const ERROR_INCORRECT_CREDENTIALS = 'Du har indtastet forkert mobilnummer eller adgangskode';
  const ERROR_CALCULATION_FAILED = 'Der er sket en fejl. Du kan prøve igen senere.';
  const ERROR_NO_DISCOUNT_PRESENT = 'Der er ingen rabat på denne mobiltelefon. Vælg en anden farve eller kapacitet';
  const ERROR_MBB_CUSTOMER = 'Du har prøvet at beholde et Internet abonnement. Det kan ikke benyttes ved køb af ny mobil.'
  const ERROR_MC_SUB_USER = 'Dit abonnement hører under en MobilChef, og du kan derfor ikke købe en mobil og beholde dit abonnement online.'

  const { track } = useTracking()
  const trackErrors = (errorMessage, errorType, errorCode) => {
    track('keepSubscriptionErrors', { errorMessage, errorType, errorCode })
  }
  /**
   * Calculates discount for keeping a subscription.
   */
  // eslint-disable-next-line complexity
  const calculateDiscountForKeepingSubscription = async () => {
    // Validate input refs
    if (!productKey.value || !sku.value || !userName.value || !password.value) {
      throw new Error(ERROR_INVALID_INPUT);
    }

    const accessToken = {
      userName: userName.value,
      password: password.value,
      accessTokenType: 'UN_PW'
    };

    try {
      loading.value = true;
      const response = await hubbleClient.discounts.getKeepSubscriptionDiscount(
        productKey.value, sku.value, cartID.value, accessToken
      );
      discountResponse.value = response.data;
      discountStore.setDiscountCalculation(discountResponse.value)
      if (discountResponse?.value?.discountPresent === false) {
        discountStore.setDiscountCalculation({})
        errorState.value = true
        noDiscountError.value = ERROR_NO_DISCOUNT_PRESENT;
        discountResponse.value = {};
        trackErrors(ERROR_NO_DISCOUNT_PRESENT, 'ERROR_NO_DISCOUNT_PRESENT', 'ERROR_NO_DISCOUNT_PRESENT')
      } else {
        errorState.value = false
        inputError.value = null
        notEligibleError.value = null
        noDiscountError.value = null
        mbbCustomerError.value = null
      }
      discountCalculationCompleted.value = true;
    } catch (err) {
      discountResponse.value = {};
      discountCalculationCompleted.value = true;
      if (err?.response?.data?.errorCode === '0007' || err?.response?.data?.errorCode === '0900-0003' || err?.response?.data?.errorCode === '0016') {
        discountCalculationCompleted.value = false;
        inputError.value = ERROR_INCORRECT_CREDENTIALS;
        trackErrors(ERROR_INCORRECT_CREDENTIALS, 'ERROR_INCORRECT_CREDENTIALS', err?.response?.data?.errorCode)
      } else if (err?.response?.data?.errorCode === '0014') {
        errorState.value = true
        mbbCustomerError.value = ERROR_MBB_CUSTOMER;
        trackErrors(ERROR_MBB_CUSTOMER, 'ERROR_MBB_CUSTOMER', err?.response?.data?.errorCode)
      } else if (err?.response?.data?.errorCode === '0015') {
        errorState.value = true
        mcSubUserError.value = ERROR_MC_SUB_USER;
        trackErrors(ERROR_MC_SUB_USER, 'ERROR_MC_SUB_USER', err?.response?.data?.errorCode)
      } else if (err?.response?.data?.errorCode === '0012') {
        errorState.value = true
        notEligibleError.value = ERROR_CALCULATION_FAILED;
        trackErrors(ERROR_CALCULATION_FAILED, 'ERROR_CALCULATION_FAILED', err?.response?.data?.errorCode)
      } else {
        errorState.value = true
        inputError.value = ERROR_CALCULATION_FAILED;
        trackErrors(ERROR_CALCULATION_FAILED, 'ERROR_CALCULATION_FAILED', err?.response?.data?.errorCode)
      }
    } finally {
      loading.value = false;
    }
  };

  /**
   * Resets the internal state to its initial values.
   */
  const resetState = () => {
    userName.value = '';
    password.value = '';
    loading.value = false;
    cartID.value = null;
    productKey.value = null;
    sku.value = null;
    inputError.value = null;
    noDiscountError.value = null;
    notEligibleError.value = null;
    mbbCustomerError.value = null;
    mcSubUserError.value = null;
    errorState.value = false;
    discountCalculationCompleted.value = false;
    discountResponse.value = {};
    discountStore.setDiscountCalculation({})
  };

  const validateKeepSubscriptionChoice = async (cartID) => {
    try {
      loading.value = true;
      return await hubbleClient.discounts.validateKeepSubscriptionDiscount(cartID);
    } catch (err) {
      console.error('Error', err)
    } finally {
      loading.value = false;
    }
    ;
  }

  return {
    calculateDiscountForKeepingSubscription,
    userName,
    password,
    loading,
    cartID,
    productKey,
    sku,
    discountCalculationCompleted,
    notEligibleError,
    inputError,
    mbbCustomerError,
    mcSubUserError,
    noDiscountError,
    errorState,
    discountResponse,
    resetState,
    getLatestDiscountCalculated: computed(() => discountStore.getLatestDiscountCalculation.value),
    validateKeepSubscriptionChoice
  };
}
